<template>
	<div>
		<div class="d-flex px-3 pb-2">
			<v-btn-toggle
				v-model="viewMode"
				mandatory
				density="compact"
				divided
				variant="outlined"
			>
				<v-btn value="users" size="small" class="text-none">
					<v-icon size="16" start>mdi-account-group</v-icon>
					By user
				</v-btn>
				<v-btn value="sdes" size="small" class="text-none">
					<v-icon size="16" start>mdi-file-document-multiple-outline</v-icon>
					All SDEs
				</v-btn>
			</v-btn-toggle>
		</div>

		<v-alert
			v-if="rollup && rollup.truncated"
			type="warning"
			variant="tonal"
			density="compact"
			class="mx-3 mb-3 text-body-2"
		>
			There are more SDEs than one report can include. Only the
			{{ rollup.sdeCount }} most recently active SDEs are counted. Narrow the
			filters for complete numbers.
		</v-alert>

		<div v-if="loading" class="d-flex justify-center pa-8">
			<v-progress-circular indeterminate color="primary" />
		</div>

		<template v-else-if="selectedSde">
			<div class="d-flex align-center ga-2 px-3 pb-2 flex-wrap">
				<v-btn
					variant="text"
					size="small"
					prepend-icon="mdi-arrow-left"
					@click="backToUsers"
				>
					{{ selectedUser ? "All users" : "All SDEs" }}
				</v-btn>
				<v-btn
					v-if="selectedUser"
					variant="text"
					size="small"
					prepend-icon="mdi-arrow-left"
					@click="backToUserSdes"
				>
					{{ selectedUser.userEmail }}
				</v-btn>
				<div class="text-subtitle-2 five-metrics-truncate">
					{{ selectedSde.paperName || "Untitled paper" }}
				</div>
				<div class="text-subtitle-2 flex-shrink-0">
					- {{ selectedSdeKind === "graphs" ? "Graphs" : "Tables" }}
				</div>
			</div>

			<div v-if="sdeBreakdownLoading" class="d-flex justify-center pa-8">
				<v-progress-circular indeterminate color="primary" />
			</div>
			<template v-else-if="sdeBreakdown">
				<v-data-table
					v-if="selectedSdeKind === 'graphs'"
					:headers="figureHeaders"
					:items="figureRows"
					density="compact"
					class="px-3 text-body-2 metric-scroll-shadow"
					:items-per-page="10"
				>
					<template
						v-for="h in figureHeaders"
						#[`header.${h.key}`]="{ column, getSortIcon }"
						:key="`head-${h.key}`"
					>
						<metric-header
							:column="column"
							:tooltip="h.tooltip"
							:get-sort-icon="getSortIcon"
						/>
					</template>

					<template #[`item.name`]="{ item }">
						<a
							v-if="sdeBreakdown.paperUuid"
							class="five-metrics-user-link"
							:href="readOnlyLink({ graphUuid: item.graphUuid })"
							target="_blank"
							rel="noopener"
							:title="item.name || item.graphUuid"
						>
							{{ item.name || shortUuid(item.graphUuid) }}
							<v-icon size="x-small" icon="mdi-open-in-new" />
						</a>
						<span v-else>{{ item.name || shortUuid(item.graphUuid) }}</span>
					</template>
					<template
						v-for="key in numericFigureColumns"
						#[`item.${key}`]="{ item }"
						:key="key"
					>
						{{ formatNumber(item[key]) }}
					</template>
					<template #no-data>
						<div class="pa-3 text-disabled">
							{{
								selectedUser
									? "No graph activity for this user on this SDE."
									: "No graph activity on this SDE."
							}}
						</div>
					</template>
				</v-data-table>

				<v-data-table
					v-else
					:headers="tableBreakdownHeaders"
					:items="tableRows"
					density="compact"
					class="px-3 text-body-2 metric-scroll-shadow"
					:items-per-page="10"
				>
					<template
						v-for="h in tableBreakdownHeaders"
						#[`header.${h.key}`]="{ column, getSortIcon }"
						:key="`head-${h.key}`"
					>
						<metric-header
							:column="column"
							:tooltip="h.tooltip"
							:get-sort-icon="getSortIcon"
						/>
					</template>

					<template #[`item.name`]="{ item }">
						<a
							v-if="sdeBreakdown.paperUuid"
							class="five-metrics-user-link"
							:href="readOnlyLink({ tableUuid: item.tableUuid })"
							target="_blank"
							rel="noopener"
							:title="item.name || item.tableUuid"
						>
							{{ item.name || shortUuid(item.tableUuid) }}
							<v-icon size="x-small" icon="mdi-open-in-new" />
						</a>
						<span v-else>{{ item.name || shortUuid(item.tableUuid) }}</span>
					</template>
					<template
						v-for="key in numericTableColumns"
						#[`item.${key}`]="{ item }"
						:key="key"
					>
						{{ formatNumber(item[key]) }}
					</template>
					<template #no-data>
						<div class="pa-3 text-disabled">
							{{
								selectedUser
									? "No table activity for this user on this SDE."
									: "No table activity on this SDE."
							}}
						</div>
					</template>
				</v-data-table>
			</template>
		</template>

		<template v-else-if="selectedUser || viewMode === 'sdes'">
			<div v-if="selectedUser" class="d-flex align-center ga-2 px-3 pb-2">
				<v-btn
					variant="text"
					size="small"
					prepend-icon="mdi-arrow-left"
					@click="backToUsers"
				>
					All users
				</v-btn>
				<div class="text-subtitle-2">
					{{ selectedUser.userEmail }}
				</div>
			</div>

			<div v-if="userSdeLoading" class="d-flex justify-center pa-8">
				<v-progress-circular indeterminate color="primary" />
			</div>
			<v-data-table
				v-else
				:headers="sdeTableHeaders"
				:items="sdeTableRows"
				density="compact"
				class="px-3 text-body-2 metric-scroll-shadow"
				:items-per-page="25"
			>
				<template
					v-for="h in sdeTableHeaders"
					#[`header.${h.key}`]="{ column, getSortIcon }"
					:key="`head-${h.key}`"
				>
					<metric-header
						:column="column"
						:tooltip="h.tooltip"
						:get-sort-icon="getSortIcon"
					/>
				</template>

				<template #[`item.paperName`]="{ item }">
					<div class="d-flex align-center ga-1">
						<a
							v-if="item.paperUuid"
							class="five-metrics-user-link d-flex align-center ga-1 overflow-hidden"
							:href="`/source-data-extraction-read-only/${item.paperUuid}/${item.dataEntryUuid}`"
							target="_blank"
							rel="noopener"
							title="Open the read-only extraction"
						>
							<span
								class="five-metrics-truncate"
								:title="item.paperName || 'Untitled paper'"
							>
								{{ item.paperName || "Untitled paper" }}
							</span>
							<v-icon
								size="x-small"
								icon="mdi-open-in-new"
								class="flex-shrink-0"
							/>
						</a>
						<div
							v-else
							class="five-metrics-truncate"
							:title="item.paperName || 'Untitled paper'"
						>
							{{ item.paperName || "Untitled paper" }}
						</div>
						<v-tooltip
							v-if="item.rework"
							location="top"
							max-width="320"
							:text="reworkTooltip(item)"
						>
							<template #activator="{ props: activator }">
								<v-chip
									v-bind="activator"
									size="x-small"
									color="warning"
									variant="tonal"
									class="flex-shrink-0"
								>
									Rework
								</v-chip>
							</template>
						</v-tooltip>
						<v-chip
							v-if="item.duplicatePaperName"
							size="x-small"
							variant="tonal"
							class="flex-shrink-0"
							:title="item.dataEntryUuid"
						>
							{{ shortUuid(item.dataEntryUuid) }}
						</v-chip>
					</div>
				</template>
				<template #[`item.figureCount`]="{ item }">
					<a
						v-if="item.figureCount > 0"
						class="five-metrics-user-link"
						role="button"
						tabindex="0"
						title="Per-graph breakdown"
						@click="selectSde(item, 'graphs')"
						@keydown.enter="selectSde(item, 'graphs')"
					>
						{{ item.figureCount }}
						<v-icon size="x-small" icon="mdi-chevron-right" />
					</a>
					<span v-else>{{ item.figureCount }}</span>
				</template>
				<template #[`item.tableCount`]="{ item }">
					<a
						v-if="item.tableCount > 0"
						class="five-metrics-user-link"
						role="button"
						tabindex="0"
						title="Per-table breakdown"
						@click="selectSde(item, 'tables')"
						@keydown.enter="selectSde(item, 'tables')"
					>
						{{ item.tableCount }}
						<v-icon size="x-small" icon="mdi-chevron-right" />
					</a>
					<span v-else>{{ item.tableCount }}</span>
				</template>
				<template
					v-for="key in numericSdeColumns"
					#[`item.${key}`]="{ item }"
					:key="key"
				>
					{{ formatNumber(item[key]) }}
				</template>
				<template #[`item.actions`]="{ item }">
					<v-btn
						icon="mdi-chart-timeline-variant"
						size="x-small"
						variant="text"
						class="text-medium-emphasis"
						title="Session timeline"
						@click="
							$emit('open-timeline', {
								userUuid: selectedUser ? selectedUser.userUuid : null,
								userEmail: selectedUser ? selectedUser.userEmail : null,
								dataEntryUuid: item.dataEntryUuid,
								paperName: item.paperName,
							})
						"
					/>
				</template>
				<template #no-data>
					<div class="pa-3 text-disabled">
						{{
							selectedUser
								? "No attributed activity for this user matches the current filters."
								: "No attributed activity matches the current filters."
						}}
					</div>
				</template>
			</v-data-table>
		</template>

		<template v-else-if="rollup">
			<v-data-table
				:headers="userHeaders"
				:items="userRows"
				:row-props="overallRowProps"
				density="compact"
				class="px-3 text-body-2 metric-scroll-shadow"
				:items-per-page="25"
			>
				<template
					v-for="h in userHeaders"
					#[`header.${h.key}`]="{ column, getSortIcon }"
					:key="`head-${h.key}`"
				>
					<metric-header
						:column="column"
						:tooltip="h.tooltip"
						:get-sort-icon="getSortIcon"
					/>
				</template>

				<template #[`item.userEmail`]="{ item }">
					<span v-if="item.isOverall" class="font-weight-bold">
						{{ item.userEmail }}
					</span>
					<a
						v-else-if="item.userUuid"
						class="five-metrics-user-link"
						role="button"
						tabindex="0"
						@click="selectUser(item)"
						@keydown.enter="selectUser(item)"
					>
						{{ item.userEmail || "deleted user" }}
					</a>
					<span v-else>{{ item.userEmail || "deleted user" }}</span>
				</template>
				<template
					v-for="key in numericUserColumns"
					#[`item.${key}`]="{ item }"
					:key="key"
				>
					<span :class="{ 'font-weight-bold': item.isOverall }">
						{{ formatNumber(item[key]) }}
					</span>
				</template>
				<template #no-data>
					<div class="pa-6 text-disabled">
						No attributed activity matches the current filters.
					</div>
				</template>
			</v-data-table>
		</template>
	</div>
</template>

<script>
import { mapActions } from "pinia";
import { useMainStore } from "@/store";

import MetricHeader from "@/pages/ActivityMetrics/MetricHeader";
import {
	toLocalDayStartIso,
	toLocalDayEndIso,
} from "@/pages/ActivityMetrics/constants";

// Filename slug: lowercase alphanumerics joined by single dashes. Long paper
// titles truncate at 40 chars, cut back to the last full word so the name
// ends cleanly; never empty.
function slugify(text, maxLength = 40) {
	let slug = String(text)
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");
	if (slug.length > maxLength) {
		slug = slug.slice(0, maxLength);
		const lastDash = slug.lastIndexOf("-");
		if (lastDash > 20) {
			slug = slug.slice(0, lastDash);
		}
	}
	return slug || "export";
}

export default {
	name: "FiveMetricsView",

	components: {
		MetricHeader,
	},

	props: {
		// Scope filters only (date/user/project). The endpoint rejects
		// event-shape filters by omission: sliced streams break the walkers.
		filters: {
			type: Object,
			required: true,
		},
		// The active uuid filters resolved to display names by index.vue,
		// used only to stamp export filenames; empty when unset/unresolved.
		filterUserEmail: {
			type: String,
			default: "",
		},
		filterProjectName: {
			type: String,
			default: "",
		},
	},

	emits: ["open-timeline", "export-busy"],

	data() {
		return {
			rollup: null,
			loading: false,
			// Top-level view switch: "users" is the per-user rollup, "sdes"
			// the flat list of every SDE with activity.
			viewMode: "users",
			// Drill-in level 2: clicking a user swaps the table for their
			// per-SDE rows.
			selectedUser: null,
			userSdeRows: [],
			userSdeLoading: false,
			// Drill-in level 3: clicking a graph or table count swaps to that
			// SDE's breakdown of the clicked kind (this user's events).
			selectedSde: null,
			// "graphs" or "tables": which breakdown level 3 shows, set by
			// which count was clicked.
			selectedSdeKind: null,
			sdeBreakdown: null,
			sdeBreakdownLoading: false,
			figureHeaders: [
				{
					title: "Graph",
					key: "name",
					sortable: false,
					tooltip: "Opens this graph in read-only SDEv2 in a new tab",
				},
				{
					title: "Active min",
					key: "activeMinutes",
					align: "end",
					tooltip: "Minutes with at least one recorded action",
				},
				{
					title: "Focus min",
					key: "focusMinutes",
					align: "end",
					tooltip: "Minutes this graph or table was the selected one",
				},
				{
					title: "Calibration min",
					key: "calibrationMinutes",
					align: "end",
					tooltip:
						"Minutes on setup: axes and series for graphs, area and dividers for tables",
				},
				{
					title: "Extraction min",
					key: "extractionMinutes",
					align: "end",
					tooltip: "Minutes placing and editing points",
				},
				{
					title: "Point events",
					key: "dataPointEventCount",
					align: "end",
					tooltip: "Point placements and edits",
				},
				{
					title: "Seconds / graph point",
					key: "secondsPerPoint",
					align: "end",
					tooltip: "Active minutes over point events, in seconds",
				},
			],
			tableBreakdownHeaders: [
				{
					title: "Table",
					key: "name",
					sortable: false,
					tooltip: "Opens this table in read-only SDEv2 in a new tab",
				},
				{
					title: "Active min",
					key: "activeMinutes",
					align: "end",
					tooltip: "Minutes with at least one recorded action",
				},
				{
					title: "Focus min",
					key: "focusMinutes",
					align: "end",
					tooltip: "Minutes this graph or table was the selected one",
				},
				{
					title: "Calibration min",
					key: "calibrationMinutes",
					align: "end",
					tooltip:
						"Minutes on setup: axes and series for graphs, area and dividers for tables",
				},
				{
					title: "Header min",
					key: "headerMinutes",
					align: "end",
					tooltip: "Minutes on table header setup",
				},
				{
					title: "Data min",
					key: "dataMinutes",
					align: "end",
					tooltip: "Minutes entering table cells",
				},
				{
					title: "Header dwell (s)",
					key: "headerDialogDwellSeconds",
					align: "end",
					tooltip: "Seconds inside header cell dialogs",
				},
				{
					title: "Cell events",
					key: "cellEventCount",
					align: "end",
					tooltip: "Cell entries, clears, and blanks",
				},
				{
					title: "Seconds / table cell",
					key: "secondsPerCell",
					align: "end",
					tooltip: "Data minutes over cell events, in seconds",
				},
			],
			// Sorting is disabled on the per-user table so the pinned "All users"
			// row cannot be shuffled into the middle; rows arrive sorted by total
			// active minutes. Columns run general -> text -> graph -> table (the
			// extraction page's tab order); each group starts with a border-s
			// separator and ends on its ratio. Table header/data minutes live at
			// level 2 and deeper, not here.
			userHeaders: [
				{
					title: "User",
					key: "userEmail",
					sortable: false,
					fixed: true,
					tooltip: "Click a user to see their time on each SDE",
				},
				{
					title: "SDEs",
					key: "unitCount",
					align: "end",
					sortable: false,
					tooltip:
						"SDE completion cycles the user worked in the current filters; work after a reopen counts as its own cycle",
				},
				{
					title: "Total active min",
					key: "totalActiveMinutes",
					align: "end",
					sortable: false,
					tooltip:
						"Minutes with at least one recorded action, summed across their SDEs; includes time in the read-only viewer, which the graph, table, and text columns do not break out",
				},
				{
					title: "Input-only min",
					key: "totalInputOnlyMinutes",
					align: "end",
					sortable: false,
					tooltip:
						"Minutes with mouse or keyboard input but no recorded action, summed across their SDEs: could be reading or scrolling within a page, could be idle mouse movement",
				},
				{
					title: "Avg active min",
					key: "avgActiveMinutes",
					align: "end",
					sortable: false,
					tooltip:
						"Mean active minutes per SDE (a minute counts once if it has at least one recorded action)",
				},
				{
					title: "Avg elapsed min",
					key: "avgElapsedMinutes",
					align: "end",
					sortable: false,
					tooltip:
						"Mean wall-clock minutes per SDE, first to last event of each sitting; the gap vs active minutes is break/idle time",
				},
				{
					title: "Avg orientation (s)",
					key: "avgOrientationSeconds",
					align: "end",
					sortable: false,
					tooltip:
						"Mean seconds from opening an SDE to the first extraction action, first session only",
				},
				{
					title: "Text min",
					key: "avgTextMinutes",
					align: "end",
					sortable: false,
					headerProps: { class: "border-s" },
					cellProps: { class: "border-s" },
					tooltip:
						"Mean active minutes per SDE on the text tab, after orientation ends",
				},
				{
					title: "Text records",
					key: "avgTextRecordCount",
					align: "end",
					sortable: false,
					tooltip: "Mean text records touched per SDE",
				},
				{
					title: "Seconds / text point",
					key: "avgSecondsPerTextPoint",
					align: "end",
					sortable: false,
					tooltip:
						"Mean seconds of text-tab work per text point added; includes the reading between points, excludes orientation",
				},
				{
					title: "Avg graphs",
					key: "avgFigureCount",
					align: "end",
					sortable: false,
					headerProps: { class: "border-s" },
					cellProps: { class: "border-s" },
					tooltip: "Mean graphs touched per SDE",
				},
				{
					title: "Minutes / graph",
					key: "avgMinutesPerFigure",
					align: "end",
					sortable: false,
					tooltip: "Mean minutes of graph work per graph",
				},
				{
					title: "Seconds / graph point",
					key: "avgSecondsPerGraphPoint",
					align: "end",
					sortable: false,
					tooltip:
						"Mean seconds of graph work per graph point placed or edited",
				},
				{
					title: "Avg tables",
					key: "avgTableCount",
					align: "end",
					sortable: false,
					headerProps: { class: "border-s" },
					cellProps: { class: "border-s" },
					tooltip: "Mean tables touched per SDE",
				},
				{
					title: "Minutes / table",
					key: "avgMinutesPerTable",
					align: "end",
					sortable: false,
					tooltip: "Mean minutes of table work per table",
				},
				{
					title: "Seconds / table cell",
					key: "avgSecondsPerTableCell",
					align: "end",
					sortable: false,
					tooltip:
						"Mean seconds of data-cell work per cell entry; header setup excluded",
				},
			],
			// Same group order as level 1: general -> text -> graph -> table.
			// The session timeline entry point is deliberately low-key (a quiet
			// untitled icon) but sits beside Paper so it never scrolls out of
			// reach on this wide table.
			sdeHeaders: [
				{
					title: "Paper",
					key: "paperName",
					fixed: true,
					tooltip: "The paper this SDE extracts",
				},
				{
					title: "",
					key: "actions",
					sortable: false,
					align: "center",
					width: 40,
				},
				{
					title: "Active min",
					key: "activeMinutes",
					align: "end",
					tooltip:
						"Minutes with at least one recorded action; includes time in the read-only viewer, which the graph, table, and text columns do not break out",
				},
				{
					title: "Input-only min",
					key: "inputOnlyMinutes",
					align: "end",
					tooltip:
						"Minutes with mouse or keyboard input but no recorded action: could be reading or scrolling within a page, could be idle mouse movement",
				},
				{
					title: "Elapsed min",
					key: "elapsedMinutes",
					align: "end",
					tooltip:
						"Wall-clock minutes, first to last event of each sitting; the gap vs active minutes is break/idle time",
				},
				{
					title: "Orientation (s)",
					key: "orientationSeconds",
					align: "end",
					tooltip: "Seconds from first open to the first extraction action",
				},
				{
					title: "Text min",
					key: "textActiveMinutes",
					align: "end",
					headerProps: { class: "border-s" },
					cellProps: { class: "border-s" },
					tooltip: "Active minutes on the text tab, after orientation ends",
				},
				{
					title: "Text records",
					key: "textRecordCount",
					align: "end",
					tooltip: "Text records touched",
				},
				{
					title: "Seconds / text point",
					key: "secondsPerTextPoint",
					align: "end",
					tooltip:
						"Text-tab seconds per text point added; includes the reading between points, excludes orientation",
				},
				{
					title: "Graphs",
					key: "figureCount",
					align: "end",
					headerProps: { class: "border-s" },
					cellProps: { class: "border-s" },
					tooltip: "Graphs touched; click for the per-graph breakdown",
				},
				{
					title: "Minutes / graph",
					key: "minutesPerFigure",
					align: "end",
					tooltip: "Graph minutes divided by graphs touched",
				},
				{
					title: "Seconds / graph point",
					key: "secondsPerGraphPoint",
					align: "end",
					tooltip: "Graph minutes per graph point placed or edited, in seconds",
				},
				{
					title: "Tables",
					key: "tableCount",
					align: "end",
					headerProps: { class: "border-s" },
					cellProps: { class: "border-s" },
					tooltip: "Tables touched; click for the per-table breakdown",
				},
				{
					title: "Minutes / table",
					key: "minutesPerTable",
					align: "end",
					tooltip: "Table minutes divided by tables touched",
				},
				{
					title: "Table header min",
					key: "headerMinutes",
					align: "end",
					tooltip: "Minutes on table header setup",
				},
				{
					title: "Table data min",
					key: "dataMinutes",
					align: "end",
					tooltip: "Minutes entering table cells",
				},
				{
					title: "Seconds / table cell",
					key: "secondsPerTableCell",
					align: "end",
					tooltip: "Data-cell seconds per cell entry; header setup excluded",
				},
			],
		};
	},

	computed: {
		// True while any level's spinner is up: the toolbar export button
		// (index.vue) disables on this, so an export always sees a settled
		// table.
		exportBusy() {
			return this.loading || this.userSdeLoading || this.sdeBreakdownLoading;
		},

		numericUserColumns() {
			return this.userHeaders
				.map((header) => header.key)
				.filter((key) => key !== "userEmail");
		},

		// The shared SDE table serves two views: a drilled-in user's rows, or
		// (in All SDEs mode) the rollup's flat per-SDE list, which gains a
		// Users column after the actions column.
		sdeTableHeaders() {
			if (this.selectedUser) {
				return this.sdeHeaders;
			}
			const headers = [...this.sdeHeaders];
			headers.splice(2, 0, {
				title: "Users",
				key: "userCount",
				align: "end",
				tooltip:
					"Users with recorded activity on this SDE; the minute columns are a union across all of them",
			});
			return headers;
		},

		sdeTableRows() {
			return this.selectedUser ? this.userSdeRows : this.sdeListRows;
		},

		// perSde rows flagged for duplicate paper names, so same-paper
		// extractions can carry a distinguishing short-uuid chip.
		sdeListRows() {
			if (!this.rollup) {
				return [];
			}
			const nameCounts = {};
			for (const row of this.rollup.perSde) {
				const name = row.paperName || "Untitled paper";
				nameCounts[name] = (nameCounts[name] || 0) + 1;
			}
			return this.rollup.perSde.map((row) => ({
				...row,
				duplicatePaperName: nameCounts[row.paperName || "Untitled paper"] > 1,
			}));
		},

		numericSdeColumns() {
			// figureCount / tableCount render through their own clickable
			// slots, so they are excluded from the generic numeric slot loop.
			return this.sdeTableHeaders
				.map((header) => header.key)
				.filter(
					(key) =>
						key !== "paperName" &&
						key !== "actions" &&
						key !== "figureCount" &&
						key !== "tableCount",
				);
		},

		numericFigureColumns() {
			return this.figureHeaders
				.map((header) => header.key)
				.filter((key) => key !== "name");
		},

		numericTableColumns() {
			return this.tableBreakdownHeaders
				.map((header) => header.key)
				.filter((key) => key !== "name");
		},

		// Ratios computed client-side from fields the API already carries;
		// per-figure uses that figure's whole active time, per-cell uses
		// DATA-kind minutes only so heading setup never inflates it.
		figureRows() {
			if (!this.sdeBreakdown) {
				return [];
			}
			return this.sdeBreakdown.perFigure.map((figure) => ({
				...figure,
				secondsPerPoint: figure.dataPointEventCount
					? Math.round(
							((figure.activeMinutes * 60) / figure.dataPointEventCount) * 10,
					  ) / 10
					: null,
			}));
		},

		tableRows() {
			if (!this.sdeBreakdown) {
				return [];
			}
			return this.sdeBreakdown.perTable.map((table) => ({
				...table,
				secondsPerCell: table.cellEventCount
					? Math.round(((table.dataMinutes * 60) / table.cellEventCount) * 10) /
					  10
					: null,
			}));
		},

		userRows() {
			if (!this.rollup) {
				return [];
			}
			// The overall row only rides along (pinned first) when there is a
			// crowd: with one user it would just duplicate them, and with no
			// users it would shadow the table's no-data state.
			if (!this.showOverallRow) {
				return [...this.rollup.perUser];
			}
			const overall = {
				...this.rollup.overall,
				userEmail: `All users (${this.rollup.overall.userCount})`,
				isOverall: true,
			};
			return [overall, ...this.rollup.perUser];
		},

		showOverallRow() {
			return Boolean(this.rollup) && this.rollup.perUser.length > 1;
		},
	},

	watch: {
		filters: {
			deep: true,
			handler() {
				// A filter change invalidates every level; return to the main
				// table so the drill-in never shows stale scope.
				this.selectedUser = null;
				this.userSdeRows = [];
				this.selectedSde = null;
				this.selectedSdeKind = null;
				this.sdeBreakdown = null;
				this.getRollup();
			},
		},

		viewMode() {
			// Switching modes jumps straight to the other view's top level;
			// both views render from the rollup already in hand, no refetch.
			this.backToUsers();
		},

		// $refs are not reactive, so the busy state travels up as an event
		// for the toolbar export button's :disabled.
		exportBusy: {
			immediate: true,
			handler(busy) {
				this.$emit("export-busy", busy);
			},
		},
	},

	mounted() {
		this.getRollup();
	},

	methods: {
		...mapActions(useMainStore, ["showNotification"]),

		// The toolbar Export button (index.vue) calls this via $refs: the
		// exact headers and rows the active v-data-table renders (single
		// source of truth, so the CSV can never drift from the display),
		// plus the filename for the view. Returns null while a spinner is
		// up or nothing has loaded. Icon-only columns (the timeline
		// button) are excluded; values the table computes in cell slots
		// (name fallbacks, the rework chip, the duplicate-paper uuid chip)
		// are materialized onto the exported rows.
		exportView() {
			if (this.exportBusy) {
				return null;
			}
			if (this.selectedSde) {
				if (!this.sdeBreakdown) {
					return null;
				}
				const figures = this.selectedSdeKind === "graphs";
				const paperSlug = slugify(
					this.selectedSde.paperName || "untitled paper",
				);
				return {
					headers: figures ? this.figureHeaders : this.tableBreakdownHeaders,
					rows: (figures ? this.figureRows : this.tableRows).map((row) => ({
						...row,
						name:
							row.name ||
							this.shortUuid(figures ? row.graphUuid : row.tableUuid),
					})),
					// Deliberately no scope suffix: this breakdown is
					// whole-history and ignores the date/user/project filters,
					// so stamping them here would mislabel the file.
					filename: `activity-${paperSlug}-${
						figures ? "figures" : "tables"
					}.csv`,
				};
			}
			if (this.selectedUser || this.viewMode === "sdes") {
				const headers = this.sdeTableHeaders.filter(
					(header) => header.key !== "actions",
				);
				if (this.selectedUser) {
					// The rework chip becomes its own column beside Paper.
					headers.splice(1, 0, { title: "Rework", key: "rework" });
					return {
						headers,
						rows: this.sdeTableRows.map((row) => ({
							...row,
							paperName: row.paperName || "Untitled paper",
							rework: row.rework ? "yes" : "",
						})),
						filename: `activity-${slugify(
							this.selectedUser.userEmail || "user",
						)}-sdes${this.exportScopeSuffix({
							// A user filter can only ever match the drilled-in
							// user, whose email already leads the filename.
							skipUser: this.filters.userUuid === this.selectedUser.userUuid,
						})}.csv`,
					};
				}
				// The duplicate-paper short-uuid chip becomes a full-uuid
				// column, so same-paper SDE rows stay tellable apart.
				headers.push({ title: "Data entry uuid", key: "dataEntryUuid" });
				return {
					headers,
					rows: this.sdeTableRows.map((row) => ({
						...row,
						paperName: row.paperName || "Untitled paper",
					})),
					filename: `activity-sdes${this.exportScopeSuffix()}.csv`,
				};
			}
			if (!this.rollup) {
				return null;
			}
			// userRows already carries the pinned "All users" row first.
			return {
				headers: this.userHeaders,
				rows: this.userRows.map((row) => ({
					...row,
					userEmail: row.userEmail || "deleted user",
				})),
				filename: `activity-users${this.exportScopeSuffix()}.csv`,
			};
		},

		// Filename suffix naming the active filters, so two exports with
		// different scopes never look interchangeable on disk. The rows
		// already respect the filters; this only labels the file. Dates are
		// the picker-format local days ("start"/"end" for an unbounded side).
		exportScopeSuffix({ skipUser = false } = {}) {
			let suffix = "";
			if (this.filters.startDate || this.filters.endDate) {
				suffix += `-${this.filters.startDate || "start"}_${
					this.filters.endDate || "end"
				}`;
			}
			if (this.filters.projectUuid && this.filterProjectName) {
				suffix += `-${slugify(this.filterProjectName)}`;
			}
			if (!skipUser && this.filters.userUuid && this.filterUserEmail) {
				suffix += `-${slugify(this.filterUserEmail)}`;
			}
			return suffix;
		},

		// Tints the pinned overall row so it reads as a summary, not a user.
		overallRowProps({ item }) {
			return item.isOverall ? { class: "metric-overall-row" } : {};
		},

		scopeParams() {
			const params = {};
			if (this.filters.projectUuid) {
				params.projectUuid = this.filters.projectUuid;
			}
			if (this.filters.userUuid) {
				params.userUuid = this.filters.userUuid;
			}
			// The pickers hold local calendar days; the API gets exact
			// day-boundary instants so the server never guesses a timezone.
			if (this.filters.startDate) {
				params.startDate = toLocalDayStartIso(this.filters.startDate);
			}
			if (this.filters.endDate) {
				params.endDate = toLocalDayEndIso(this.filters.endDate);
			}
			return params;
		},

		getRollup() {
			this.loading = true;

			this.$http
				.get("/activity-events/metrics-rollup", {
					params: this.scopeParams(),
				})
				.then((response) => {
					this.rollup = response.data.metricsRollup;
				})
				.catch((error) => {
					this.reportError(error);
				})
				.finally(() => {
					this.loading = false;
				});
		},

		selectUser(row) {
			this.selectedUser = {
				userUuid: row.userUuid,
				userEmail: row.userEmail,
			};
			this.getUserSdes(row.userUuid);
		},

		// Back to the current view's top level: the user rollup in By user
		// mode, the flat SDE list in All SDEs mode.
		backToUsers() {
			this.selectedUser = null;
			this.userSdeRows = [];
			this.selectedSde = null;
			this.selectedSdeKind = null;
			this.sdeBreakdown = null;
		},

		backToUserSdes() {
			this.selectedSde = null;
			this.selectedSdeKind = null;
			this.sdeBreakdown = null;
		},

		selectSde(row, kind) {
			this.selectedSde = {
				dataEntryUuid: row.dataEntryUuid,
				paperName: row.paperName,
			};
			this.selectedSdeKind = kind;
			this.getSdeBreakdown(row.dataEntryUuid);
		},

		getSdeBreakdown(dataEntryUuid) {
			this.sdeBreakdownLoading = true;
			this.sdeBreakdown = null;

			this.$http
				.get("/activity-events/sde-metrics", {
					// Restricted to the drilled-in user when there is one; from
					// the All SDEs list the breakdown covers every user's time.
					params: {
						dataEntryUuid,
						...(this.selectedUser
							? { userUuid: this.selectedUser.userUuid }
							: {}),
					},
				})
				.then((response) => {
					this.sdeBreakdown = response.data.sdeMetrics;
				})
				.catch((error) => {
					this.reportError(error);
					this.backToUserSdes();
				})
				.finally(() => {
					this.sdeBreakdownLoading = false;
				});
		},

		// Open the source container in SDEv2 read-only, in a new tab. focus
		// = { graphUuid } or { tableUuid }; SourceDataExtraction's deep-link
		// handler selects it on load. (/data-entry-read-only is the SDEv1
		// page - wrong surface for this telemetry.)
		readOnlyLink(focus) {
			const base = `/source-data-extraction-read-only/${this.sdeBreakdown.paperUuid}/${this.selectedSde.dataEntryUuid}`;
			const key = Object.keys(focus)[0];
			return `${base}?${key}=${encodeURIComponent(focus[key])}`;
		},

		// Real uuids shorten to their first block; anything else (the
		// backend's synthetic "deleted-sde-<id>" keys) stays whole, since its
		// distinctive part is the trailing id and a front slice would render
		// every such key the same.
		shortUuid(value) {
			if (!value) {
				return "none";
			}
			return value.length === 36 ? value.slice(0, 8) : value;
		},

		getUserSdes(userUuid) {
			this.userSdeLoading = true;
			this.userSdeRows = [];

			this.$http
				.get("/activity-events/metrics-rollup", {
					// Scoped to one user, perUnit rows are exactly that user's
					// (SDE, cycle) units: one row per completion cycle, so an
					// SDE the user worked before AND after a reopen shows two
					// rows (the rework one chipped). Rows arrive with same-SDE
					// cycles adjacent, original first.
					params: { ...this.scopeParams(), userUuid },
				})
				.then((response) => {
					this.userSdeRows = response.data.metricsRollup.perUnit;
				})
				.catch((error) => {
					this.reportError(error);
					this.backToUsers();
				})
				.finally(() => {
					this.userSdeLoading = false;
				});
		},

		// "After reopen on <date> by <email>"; the reopener's email can be
		// missing (deleted user), in which case the date stands alone.
		reworkTooltip(item) {
			const date = item.reopenedAt
				? new Date(item.reopenedAt).toLocaleDateString()
				: "an unknown date";
			return item.reopenedByEmail
				? `After reopen on ${date} by ${item.reopenedByEmail}`
				: `After reopen on ${date}`;
		},

		reportError(error) {
			if (error.response?.data?.errors?.length) {
				this.showNotification({
					message: error.response.data.errors[0].message,
					status: "error",
				});
			} else {
				this.showNotification({
					message: "Could not load activity metrics.",
					status: "error",
				});
			}
		},

		formatNumber(value) {
			if (value === null || value === undefined) {
				return "-";
			}
			return value;
		},
	},
};
</script>

<style scoped>
.five-metrics-truncate {
	/* Tight cap: papers stay recognizable from the lead-in and the full
	   name is in the hover tooltip; the width goes to the metric columns. */
	max-width: 200px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.five-metrics-user-link {
	color: rgb(var(--v-theme-primary));
	cursor: pointer;
	text-decoration: underline;
}

/* Vuetify's 16px cell side-padding costs ~450px across these 14+ column
   tables; 8px keeps them readable and buys half the overflow back. The
   full chain out-specifies Vuetify's density selector. */
.metric-scroll-shadow :deep(.v-table__wrapper > table > thead > tr > th),
.metric-scroll-shadow :deep(.v-table__wrapper > table > tbody > tr > td) {
	padding: 0 8px;
}

/* Scroll-aware edge fades on the wide tables: the surface-colored covers
   scroll with the content (attachment local) while the shadows stay put
   (attachment scroll), so a shadow shows only while that edge clips. */
.metric-scroll-shadow :deep(.v-table__wrapper) {
	background:
		linear-gradient(
				to right,
				rgb(var(--v-theme-surface)) 30%,
				rgba(var(--v-theme-surface), 0)
			)
			left / 40px 100%,
		linear-gradient(
				to left,
				rgb(var(--v-theme-surface)) 30%,
				rgba(var(--v-theme-surface), 0)
			)
			right / 40px 100%,
		radial-gradient(
				farthest-side at 0 50%,
				rgba(var(--v-theme-on-surface), 0.22),
				transparent
			)
			left / 12px 100%,
		radial-gradient(
				farthest-side at 100% 50%,
				rgba(var(--v-theme-on-surface), 0.22),
				transparent
			)
			right / 12px 100%;
	background-repeat: no-repeat;
	background-attachment: local, local, scroll, scroll;
}

/* Opaque tint (gradient over surface) so the sticky User cell never lets
   horizontally scrolled content bleed through the pinned row. */
.metric-scroll-shadow :deep(tr.metric-overall-row > td) {
	background: linear-gradient(
			rgba(var(--v-theme-on-surface), 0.05),
			rgba(var(--v-theme-on-surface), 0.05)
		),
		rgb(var(--v-theme-surface));
}
</style>
